import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Avatar } from "./Avatar";
import { Icon } from "../common/Icon";

interface User {
  username: string;
  role: string;
  /** Provider photo, when the reader has chosen to show one. */
  avatarUrl?: string;
}

interface UserMenuProps {
  user: User;
  isAdmin: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onLogout: () => void;
  onClose: () => void;
}

export function UserMenu({ user, isAdmin, isOpen, onToggle, onLogout, onClose }: UserMenuProps) {
  const root = useRef<HTMLDivElement>(null);

  /**
   * A menu that only closes by clicking its own trigger is a trap — every
   * other menu on the web closes when you click away or press Escape, and
   * people reasonably expect this one to as well.
   *
   * `pointerdown` rather than `click`: it fires before focus moves, so the
   * menu is gone by the time whatever was clicked reacts.
   */
  useEffect(() => {
    if (!isOpen) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) onClose();
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);

  return (
    <div className="relative" ref={root}>
      <button
        onClick={onToggle}
        className="flex items-center gap-2 rounded-full focus:outline-none focus:ring-2 focus:ring-primary/40"
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={`User menu for ${user.username}`}
      >
        <Avatar username={user.username} src={user.avatarUrl} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-52 rounded-xl border border-border bg-background shadow-lg py-1 z-50"
          role="menu"
        >
          {/* User header row */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-border/60">
            <Avatar username={user.username} src={user.avatarUrl} size="sm" className="shrink-0" />
            <span className="text-sm font-semibold text-foreground truncate">{user.username}</span>
          </div>

          <Link
            to="/settings"
            role="menuitem"
            onClick={onClose}
            className="flex items-center gap-2 w-full px-4 py-2.5 text-sm hover:bg-muted transition-colors text-foreground"
          >
            <Icon name="user" size={16} className="text-muted-foreground" />
            Profile &amp; settings
          </Link>

          {isAdmin && (
            <Link
              to="/admin"
              role="menuitem"
              onClick={onClose}
              className="flex items-center gap-2 w-full px-4 py-2.5 text-sm hover:bg-muted transition-colors text-foreground"
            >
              <Icon name="settings" size={16} className="text-muted-foreground" />
              Admin Dashboard
            </Link>
          )}

          <div className="border-t border-border/60 mt-1" />

          <button
            role="menuitem"
            onClick={onLogout}
            className="flex items-center gap-2 w-full px-4 py-2.5 text-sm hover:bg-muted transition-colors text-foreground mt-1"
          >
            <Icon name="logout" size={16} className="text-muted-foreground" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}